import type { NodeId, NodeMap, TagEdges, TagId, TagMap } from './types';

/**
 * The tag tree.
 *
 * Tags form their own hierarchy, inverted the same way node edges are: `tagEdges` maps a tag
 * to its parent, and a top-level tag has no entry.
 *
 * This module deliberately imports NOTHING from `lib/permissions.ts`, even though both
 * `tagCount` and `isTagVisible` need to know what is hidden. Permissions expands hidden tags
 * through `tagDescendants` from here, so importing back would make a cycle. The dependency
 * runs one way — permissions -> tags — and the callers here take a predicate or a set.
 *
 * NOTE `tag.collapsed` is NOT per-user. It is tag structure and syncs to the server, so one
 * person collapsing a group collapses it for everybody. That is the explanation when the
 * sidebar seems to change on its own, and it is deliberate — unlike node collapse, which is
 * personal and day-scoped.
 */

/**
 * Direct child tags of `pid` (`null` for top level), in display order.
 *
 * `order` is set explicitly by dragging. Tags never dragged share `order` 0 and therefore
 * keep their insertion order, because `Array.prototype.sort` is stable — which is the
 * behaviour the sidebar has always had.
 */
export function tagChildren(tags: TagMap, tagEdges: TagEdges, pid: TagId | null): TagId[] {
  const out: TagId[] = [];
  for (const t in tags) {
    const parent = t in tagEdges ? tagEdges[t] : null;
    if (parent === pid) out.push(t);
  }
  out.sort((a, b) => (tags[a].order ?? 0) - (tags[b].order ?? 0));
  return out;
}

/** Every tag below `tid`, at any depth. */
export function tagDescendants(tagEdges: TagEdges, tid: TagId): TagId[] {
  const out: TagId[] = [];
  const stack: TagId[] = [tid];
  const seen = new Set<TagId>();
  while (stack.length) {
    const x = stack.pop()!;
    for (const t in tagEdges) {
      if (tagEdges[t] === x && !seen.has(t)) {
        seen.add(t);
        out.push(t);
        stack.push(t);
      }
    }
  }
  return out;
}

/** This tag's parent, or `null` at the top level. The one place `tagEdges`' inversion is read. */
export function tagParent(tagEdges: TagEdges, tid: TagId): TagId | null {
  return tid in tagEdges ? tagEdges[tid] : null;
}

/**
 * The ancestors of `tid`, outermost first — `['MDM', 'PoSt', 'Entity']` for `Attribute`.
 *
 * NOT including the tag itself, so an empty array means "top level". `tags` is taken but not
 * read: the signature matches `tagChildren`'s so callers do not have to remember which of the
 * two containers each helper wants, and a future display order for ancestors would need it.
 */
export function tagPath(_tags: TagMap, tagEdges: TagEdges, tid: TagId): TagId[] {
  const out: TagId[] = [];
  let p = tagParent(tagEdges, tid);
  let guard = 0;
  while (p !== null && guard++ < 10_000) {
    out.unshift(p);
    p = tagParent(tagEdges, p);
  }
  return out;
}

/** How deep this tag sits. 0 at the top level — what an indent is measured in. */
export function tagDepth(tagEdges: TagEdges, tid: TagId): number {
  let d = 0;
  let p = tagParent(tagEdges, tid);
  while (p !== null && d < 10_000) {
    d += 1;
    p = tagParent(tagEdges, p);
  }
  return d;
}

/** The top-level tags, in display order. `tagChildren(tags, tagEdges, null)` under its own name. */
export function tagRoots(tags: TagMap, tagEdges: TagEdges): TagId[] {
  return tagChildren(tags, tagEdges, null);
}

/**
 * Which tags a search shows: the ones whose NAME matches, plus every ancestor of each.
 *
 * The ancestors are the point. A flat list of hits answers "which tags match" but throws away
 * the thing the hierarchy is for — searching "attr" should show `MDM › PoSt › Entity ›
 * Attribute` in its place in the tree, so the reader can see what kind of attribute it is
 * before clicking. The ancestors are included as CONTEXT, and the picker draws them unfolded
 * and un-highlighted; they are not themselves matches.
 *
 * `null` for an empty query means "no filter at all", which is a different answer from "every
 * tag matched" and saves every caller a branch.
 */
export function tagMatchSet(tags: TagMap, tagEdges: TagEdges, query: string): Set<TagId> | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const out = new Set<TagId>();
  for (const t in tags) {
    if (!(tags[t].name || '').toLowerCase().includes(q)) continue;
    out.add(t);
    for (const p of tagPath(tags, tagEdges, t)) out.add(p);
  }
  return out;
}

/** Is `a` an ancestor of `t`? Used to refuse a drag that would make the tree a cycle. */
export function isTagAncestor(tagEdges: TagEdges, a: TagId, t: TagId): boolean {
  let p = tagEdges[t];
  let guard = 0;
  while (p !== undefined && guard++ < 10_000) {
    if (p === a) return true;
    p = tagEdges[p];
  }
  return false;
}

/**
 * How many nodes carry this tag — the number beside each row in the sidebar.
 *
 * Counts DIRECT tags only, not tags inherited from a sub-tag, matching the vanilla
 * behaviour: tag inheritance is not resolved client-side anywhere yet.
 *
 * `isNodeHidden` skips permission-hidden nodes, so the count never reveals the size of a
 * branch the user cannot see. Pass `() => false` when counting without a permission context.
 */
export function tagCount(
  tid: TagId,
  nodes: NodeMap,
  isNodeHidden: (id: NodeId) => boolean,
): number {
  let c = 0;
  for (const id in nodes) {
    if (isNodeHidden(id)) continue;
    if (nodes[id].tags?.includes(tid)) c++;
  }
  return c;
}

/**
 * Is this tag shown at all?
 *
 * Two separate reasons it might not be, and they are unrelated. `invisible` is tag
 * structure — an admin marking a tag as internal bookkeeping, hidden from everyone outside
 * edit mode. Permission-hidden is per group. Both have to pass.
 */
export function isTagVisible(
  tag: { invisible?: boolean } | undefined,
  tagId: TagId,
  opts: { hiddenTags: ReadonlySet<TagId>; editing: boolean; canEdit: boolean },
): boolean {
  if (!tag) return false;
  if (opts.hiddenTags.has(tagId)) return false;
  if (tag.invisible && !(opts.editing && opts.canEdit)) return false;
  return true;
}

// The "No doc" tag is RETIRED (V0.42). "This node needs no documentation" is now said through
// the documentation plan (`expectedDocs = {}`), not an ordinary tag matched by name. The
// predicate moved to `isExemptPlan()` in lib/health.ts; `hasNoDocTag`/`NO_DOC_TAG_NAME` are gone.
